import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ShareUnit, ShareUnitToggle, StatTabs } from '@digital-twins/deck-ui/plots/_shared'
import {
  DivergingLikertPlot,
  DivergingLikertScaleKey,
  DivergingLikertStoredData,
  divergingLikertActiveView,
  divergingLikertScaleKey,
  divergingLikertStats,
  divergingLikertTabLabels,
} from '@digital-twins/deck-ui/plots/diverging-likert'

import { FigurePanel, FigurePanelSections, FigureViewProps, useShareUnitLabels } from './figureViewShared'

import { PlotStatView } from '@digital-twins/slide-schemas/generated/plot-types'

/**
 * `diverging-likert` on the survey tab draws the kind's plot
 * (`DivergingLikertPlot`, plots plan P): headline tabs (Average | Top N, or
 * Average | NPS), the Top N view's grouped bar, counts and base, and a pair's
 * before / after rows with the change and its test. Every figure is Python's.
 *
 * One set of tabs for the card: cut panels share the active view, so each
 * group reads on the same statistic. The tab and Average words fall back to
 * the app's viewer-language keys for a payload stored without them.
 *
 * A shared scale also prints its ends above the bars, in the heat-grid key's
 * form ("1 = Not at all clear", and on Top N "Top 2 (4 and 5), 5 = Completely
 * clear"). Panels that do not share one pair of ends print no key.
 */

function panelData(panel: FigurePanel): DivergingLikertStoredData {
  return panel.plot.data as unknown as DivergingLikertStoredData
}

function sharedScaleKey(panels: Array<FigurePanel>, view: PlotStatView): DivergingLikertScaleKey | null {
  const ok = panels.map(panelData).filter((data) => data.status === 'ok')
  if (ok.length === 0) {
    return null
  }
  const keys = ok.map((data) => divergingLikertScaleKey(data, view))
  const first = keys[0]
  if (!first) {
    return null
  }
  const matches = keys.every((key) => key?.low === first.low && key?.high === first.high)
  if (!matches) {
    return null
  }
  return first
}

export function DivergingLikertFigure(props: FigureViewProps) {
  const { panels, isCut, locale, visibleDenominator = null } = props

  const { t } = useTranslation('app')

  const [requestedView, setRequestedView] = useState<PlotStatView | null>(null)
  const [shareUnit, setShareUnit] = useState<ShareUnit>('percent')
  const shareUnitLabels = useShareUnitLabels()

  const testIdPrefix = 'figure-diverging-likert'
  const tabbed = panels.map(panelData).find((data) => data.status === 'ok' && divergingLikertStats(data))
  const fallback = {
    average: t('studyReport.numericChart.average'),
    nps: t('studyReport.statTabNps'),
  }
  const view = tabbed ? divergingLikertActiveView(tabbed, requestedView) : 'mean'
  const stats = tabbed ? divergingLikertStats(tabbed) : null

  const tabs = tabbed && stats ? (
    <StatTabs
      stats={stats}
      activeView={view}
      labels={divergingLikertTabLabels(tabbed, fallback)}
      onViewChange={setRequestedView}
    />
  ) : null
  const hasPlot = panels.some((panel) => panelData(panel).status === 'ok')
  const unit = hasPlot ? (
    <ShareUnitToggle value={shareUnit} onChange={setShareUnit} labels={shareUnitLabels} testIdPrefix={`${testIdPrefix}-share-unit-`} />
  ) : null
  const scaleKey = sharedScaleKey(panels, view)
  const scaleKeyNode = scaleKey ? (
    <div
      className="ml-auto flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] leading-tight text-muted-foreground"
      data-testid={`${testIdPrefix}-scale-key`}
    >
      {scaleKey.low ? <span data-testid={`${testIdPrefix}-scale-key-low`}>{scaleKey.low}</span> : null}
      {scaleKey.high ? <span data-testid={`${testIdPrefix}-scale-key-high`}>{scaleKey.high}</span> : null}
    </div>
  ) : null

  return (
    <div className="space-y-4" data-testid={testIdPrefix}>
      {(tabs || unit || scaleKeyNode) && (
        <div className="flex flex-wrap items-center gap-2">
          {tabs}
          {props.controlsPlacement !== 'below' ? unit : null}
          {scaleKeyNode}
        </div>
      )}
      <FigurePanelSections
        panels={panels}
        isCut={isCut}
        testIdPrefix={testIdPrefix}
        lowBaseLabel={(panel) => panelData(panel).labels?.plot_low_base}
        renderPanel={(panel, index) => (
          <DivergingLikertPlot
            data={panelData(panel)}
            activeView={view}
            locale={locale}
            tabLabelFallback={fallback}
            showTabs={false}
            shareUnit={shareUnit}
            visibleDenominator={visibleDenominator}
            barHeightPx={20}
            fastHover
            showScaleEnds
            testIdPrefix={isCut ? `${testIdPrefix}-${index}` : testIdPrefix}
          />
        )}
      />
      {props.controlsPlacement === 'below' && unit ? <div className="flex justify-end">{unit}</div> : null}
    </div>
  )
}
